'use client';

import { TourProvider as ReactTourProvider, type StepType } from '@reactour/tour';
import type { FunctionComponent } from 'react';

import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useTakeATourInit } from '../take-a-tour/hooks/use-tour-data-init';
import { useCheckTranslatedString } from '../utils/custom-hooks/use-check-translated-string';
import { useDevice } from '../utils/custom-hooks/use-device';
import { useTheme } from '../utils/custom-hooks/use-theme';

export const TourProvider: FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const { tourSteps, isPending, error } = useTakeATourInit();
  const { isDarkTheme } = useTheme();
  const { isLaptop } = useDevice();
  const checkTranslatedString = useCheckTranslatedString();

  if (isPending || error) {
    return <>{children}</>;
  }

  const steps: StepType[] = tourSteps
    ? tourSteps.map((step) => {
        if (isLaptop && step.mobileSelector)
          return {
            selector: step.mobileSelector ?? '',
            content: () => (
              <Paragraph variant='text-base' className='m-3'>
                {checkTranslatedString(step.content)}
              </Paragraph>
            ),
            position: step.position ?? 'bottom'
          };

        return {
          selector: step.selector ?? '',
          content: () => (
            <Paragraph variant='text-base' className='m-3'>
              {checkTranslatedString(step.content)}
            </Paragraph>
          ),
          position: step.position ?? 'right'
        };
      })
    : [];

  return (
    <ReactTourProvider
      steps={steps}
      startAt={0}
      position={'right'}
      styles={{
        popover: (base) => ({
          ...base,
          '--reactour-accent': '#FF000F',
          borderRadius: 8,
          backgroundColor: isDarkTheme ? '#2A2A2A' : '#F5F5F5'
        }),
        maskArea: (base) => ({ ...base, rx: 8 }),
        button: (base) => ({ ...base, color: isDarkTheme ? '#F1F1F1' : '#1F1F1F' })
      }}
    >
      {children}
    </ReactTourProvider>
  );
};
